import { Card, Link, Skeleton } from "@heroui/react";
import type { ReactNode } from "react";

type PanelProps = {
  title: string;
  /** L'écran qui porte la liste complète. */
  href: string;
  seeAll: string;
  /**
   * Nom accessible du lien « tout voir ».
   *
   * ⚠️ Trois panneaux, trois liens au même libellé : dans la liste des liens
   * d'un lecteur d'écran, ils seraient indiscernables sans ce nom-là.
   */
  seeAllLabel: string;
  isLoading: boolean;
  isError: boolean;
  isEmpty: boolean;
  emptyLabel: string;
  errorLabel: string;
  /** Nombre de lignes fantômes pendant le chargement. */
  rows: number;
  children: ReactNode;
};

/**
 * La coque commune des trois listes du tableau de bord.
 *
 * Un titre, un lien vers l'écran complet, et les quatre états qu'une liste
 * distante peut prendre. Écrite une fois : trois copies auraient fini par
 * diverger sur le seul état qu'on oublie toujours — l'échec, qui sans cette
 * branche se lirait comme une liste vide, c'est-à-dire comme la plus
 * rassurante des contre-vérités.
 */
export function Panel({
  title,
  href,
  seeAll,
  seeAllLabel,
  isLoading,
  isError,
  isEmpty,
  emptyLabel,
  errorLabel,
  rows,
  children,
}: PanelProps) {
  const notice = isError ? errorLabel : isEmpty ? emptyLabel : null;

  return (
    <Card className="flex min-w-0 flex-col gap-3 p-4">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="text-sm font-semibold">{title}</h2>

        <Link aria-label={seeAllLabel} className="text-xs" href={href}>
          {seeAll}
        </Link>
      </div>

      {isLoading ? (
        <div aria-hidden className="flex flex-col gap-2">
          {Array.from({ length: rows }, (_unused, row) => (
            <Skeleton className="h-10 w-full rounded-lg" key={row} />
          ))}
        </div>
      ) : notice ? (
        <p className="py-6 text-center text-sm text-pretty text-muted">
          {notice}
        </p>
      ) : (
        <ul className="flex flex-col divide-y divide-separator">{children}</ul>
      )}
    </Card>
  );
}
